import { useCallback, useEffect, useState } from 'react';
import { getLscLeagueSetupProgress, patchLscLeagueSetupProgress, LscApiError } from '../api';
import type { LscLeagueSetupProgress, LscLeagueSetupProgressStepId } from '../types';

export function useLeagueSetupProgress(leagueId: string | undefined) {
  const [progress, setProgress] = useState<LscLeagueSetupProgress | null>(null);
  const [loading, setLoading] = useState(Boolean(leagueId));
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastFailedStepId, setLastFailedStepId] = useState<LscLeagueSetupProgressStepId | null>(
    null
  );
  const [authError, setAuthError] = useState<'unauthorized' | 'forbidden' | null>(null);

  const reload = useCallback(async () => {
    if (!leagueId) {
      setProgress(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setAuthError(null);
    try {
      const next = await getLscLeagueSetupProgress(leagueId);
      setProgress(next);
    } catch (err) {
      if (err instanceof LscApiError) {
        if (err.status === 401) {
          setAuthError('unauthorized');
        } else if (err.status === 403) {
          setAuthError('forbidden');
        } else {
          setError(err.message);
        }
      } else {
        setError('Unable to load setup progress.');
      }
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const saveCurrentStep = useCallback(
    async (currentStepId: LscLeagueSetupProgress['current_step_id']) => {
      if (!leagueId) {
        return null;
      }
      setSaveError(null);
      try {
        const next = await patchLscLeagueSetupProgress(leagueId, { current_step_id: currentStepId });
        setProgress(next);
        setLastFailedStepId(null);
        return next;
      } catch (err) {
        setLastFailedStepId(currentStepId);
        if (err instanceof LscApiError) {
          setSaveError(err.message);
        } else {
          setSaveError('Unable to save setup progress.');
        }
        return null;
      }
    },
    [leagueId]
  );

  return {
    progress,
    loading,
    error,
    saveError,
    lastFailedStepId,
    authError,
    reload,
    saveCurrentStep,
  };
}
